import React, {
  createContext,
  useContext,
  forwardRef,
  ReactNode,
  isValidElement,
  useRef,
  useEffect,
  useLayoutEffect,
} from 'react';
import {
  TextField,
  TextFieldProps,
  Input as AriaInput,
  InputProps as AriaInputProps,
  ValidationResult,
} from 'react-aria-components';
import { FormValidationContext } from 'react-stately';
import styles from './Input.module.css';
import inputStyles from '../../../utils/forms/input/input.module.css';
import FieldHeader from '../../FieldHeader/FieldHeader';
import IconButton, { IconButtonProps } from '../../IconButton/IconButton';
import { Button, ButtonProps } from '../../Button/Button';
import FieldError from '../../FieldError';
import {
  inputFieldVariants,
  type InputFieldVariants,
} from '../../../utils/forms/input/input';
import {
  getBaseValue,
  generateResponsiveClasses,
  type WithResponsiveProps,
} from '../../../utils/breakpoint/responsiveSSR';
import { useBreakpoint } from '../../../utils/breakpoint/hooks/useBreakpoint';
import { resolveResponsiveProp } from '../../../utils/breakpoint/responsive';
import { Icon, IconProps } from '../../Icon/Icon';
import { altoConsole } from '../../../utils/console/console';
import { resolveValidationState } from '../../../utils/forms/resolveValidationState';

// Type guard to check if a ReactNode is an IconButton component
function isIconButtonElement(
  node: ReactNode,
): node is React.ReactElement<IconButtonProps> {
  return (
    isValidElement(node) &&
    (node.type === IconButton ||
      (typeof node.type === 'function' && node.type.name === 'IconButton'))
  );
}
// Type guard to check if a ReactNode is a Button component
function isButtonElement(
  node: ReactNode,
): node is React.ReactElement<ButtonProps> {
  return (
    isValidElement(node) &&
    (node.type === Button ||
      (typeof node.type === 'function' && node.type.name === 'Button'))
  );
}
// Type guard to check if a ReactNode is an Icon component
function isIconElement(node: ReactNode): node is React.ReactElement<IconProps> {
  return (
    isValidElement(node) &&
    (node.type === Icon ||
      (typeof node.type === 'function' && node.type.name === 'Icon'))
  );
}
// Comprehensive width measurement function
// Memoization cache for measureWidth per render batch
let measureWidthCache: WeakMap<HTMLElement, number> | null = null;

const startMeasureWidthBatch = () => {
  measureWidthCache = new WeakMap();
};

const endMeasureWidthBatch = () => {
  measureWidthCache = null;
};

const measureWidth = (ref: React.RefObject<HTMLDivElement>): number => {
  if (!ref.current) return 0;
  if (measureWidthCache) {
    const cached = measureWidthCache.get(ref.current);
    if (typeof cached === 'number') return cached;
  }
  // Multiple strategies for width measurement
  const strategies = [
    () => ref.current?.offsetWidth || 0,
    () => ref.current?.clientWidth || 0,
    () => ref.current?.scrollWidth || 0,
    () =>
      Array.from(ref.current?.children || []).reduce(
        (max, child) => Math.max(max, child.clientWidth),
        0,
      ),
  ];
  // Try strategies, return first non-zero result
  for (const strategy of strategies) {
    const width = strategy();
    if (width > 0) {
      if (measureWidthCache) measureWidthCache.set(ref.current, width);
      return width;
    }
  }
  if (measureWidthCache) measureWidthCache.set(ref.current, 0);
  return 0;
};

export type InputContextType = {
  /**
   * Whether the input is disabled
   */
  isDisabled?: boolean;

  /**
   * Whether the input is invalid
   */
  isInvalid?: boolean;

  /**
   * Input type
   */
  type?: string;

  /**
   * Unique identifier for accessibility
   */
  inputId?: string;
};

export const InputContext = createContext<InputContextType>({});

// Prefix Subcomponent (Strings only)
interface PrefixProps {
  children: string;
  visuallyHiddenLabel?: string;
}

const Prefix = forwardRef<HTMLDivElement, PrefixProps>(
  ({ children, visuallyHiddenLabel }, ref) => {
    const state = useContext(InputContext);

    if (typeof children !== 'string') {
      altoConsole.error(
        'Input.Prefix only accepts a single string as a child',
        { scope: 'Input' },
      );
    }

    return (
      <div
        ref={ref}
        className={styles['input-field__prefix']}
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
        data-prefix-type="text"
        aria-hidden={visuallyHiddenLabel ? 'true' : undefined}
      >
        {children}
      </div>
    );
  },
);
Prefix.displayName = 'Input.Prefix';

// Suffix Subcomponent (Strings only)
interface SuffixProps {
  children: string;
  visuallyHiddenLabel?: string;
}

const Suffix = forwardRef<HTMLDivElement, SuffixProps>(
  ({ children, visuallyHiddenLabel }, ref) => {
    const state = useContext(InputContext);

    if (typeof children !== 'string') {
      altoConsole.error(
        'Input.Suffix only accepts a single string as a child',
        { scope: 'Input' },
      );
    }

    return (
      <div
        ref={ref}
        className={styles['input-field__suffix']}
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
        data-suffix-type="text"
        aria-hidden={visuallyHiddenLabel ? 'true' : undefined}
      >
        {children}
      </div>
    );
  },
);
Suffix.displayName = 'Input.Suffix';

// Shared type for components with children
interface SlotProps {
  children: React.ReactNode;
  className?: string;
  increment?: boolean | object;
  decrement?: boolean | object;
}

// Generic function to handle disabled state and icon color
const passthroughChildrenWithDisabledState = (
  children: React.ReactNode,
  isDisabled?: boolean,
) => {
  const getIconEndProps = (child: React.ReactElement) => {
    if (!child.props.iconEndProps) return {};
    if (isDisabled) {
      return {
        iconEndProps: {
          ...child.props.iconEndProps,
          color: 'disabledPrimary',
        },
      };
    }
    return { iconEndProps: child.props.iconEndProps };
  };

  const isInteractiveElement = (type: unknown) =>
    typeof type === 'string' &&
    ['button', 'input', 'select', 'textarea'].includes(type);

  return React.Children.map(children, (child) => {
    if (!React.isValidElement(child)) return child;

    if (isButtonElement(child)) {
      return React.cloneElement(child, {
        isDisabled,
        ...getIconEndProps(child),
      });
    }

    if (isIconElement(child)) {
      return React.cloneElement(child, {
        color: isDisabled ? 'disabledPrimary' : child.props.color,
      });
    }

    if (isIconButtonElement(child)) {
      return React.cloneElement(child, {
        isDisabled,
      });
    }

    if (isInteractiveElement(child.type)) {
      return React.cloneElement(child, {
        disabled: isDisabled,
      });
    }

    // Add 'data-disabed' if we don't know the element's type
    return React.cloneElement(child, {
      'data-disabled': isDisabled,
    });
  });
};

// StartSlot Subcomponent
const StartSlot = forwardRef<HTMLDivElement, SlotProps>(
  ({ children, className, increment, decrement }, ref) => {
    const state = useContext(InputContext);
    let processedChildren = passthroughChildrenWithDisabledState(
      children,
      state.isDisabled,
    );

    // If increment or decrement prop is present, clone the button child with those props
    if (increment || decrement) {
      processedChildren = React.Children.map(children, (child) => {
        if (
          React.isValidElement(child) &&
          (isButtonElement(child) || isIconButtonElement(child))
        ) {
          return React.cloneElement(child, {
            type: 'button',
            ...(typeof increment === 'object' && increment !== null
              ? increment
              : {}),
            ...(typeof decrement === 'object' && decrement !== null
              ? decrement
              : {}),
          });
        }
        return child;
      });
    }

    return (
      <div
        ref={ref}
        className={`
          ${styles['input-field__startSlot']} 
          ${className || ''}
        `}
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
        data-start-slot-button={
          React.Children.toArray(children).some(isButtonElement)
            ? 'true'
            : undefined
        }
      >
        {processedChildren}
      </div>
    );
  },
);
StartSlot.displayName = 'Input.StartSlot';

// EndSlot Subcomponent
const EndSlot = forwardRef<HTMLDivElement, SlotProps>(
  ({ children, className, increment, decrement }, ref) => {
    const state = useContext(InputContext);
    let processedChildren = passthroughChildrenWithDisabledState(
      children,
      state.isDisabled,
    );

    // If increment or decrement prop is present, clone the button child with those props
    if (increment || decrement) {
      processedChildren = React.Children.map(children, (child) => {
        if (
          React.isValidElement(child) &&
          (isButtonElement(child) || isIconButtonElement(child))
        ) {
          return React.cloneElement(child, {
            type: 'button',
            ...(typeof increment === 'object' && increment !== null
              ? increment
              : {}),
            ...(typeof decrement === 'object' && decrement !== null
              ? decrement
              : {}),
          });
        }
        return child;
      });
    }

    return (
      <div
        ref={ref}
        className={[styles['input-field__endSlot'], className || '']
          .filter(Boolean)
          .join(' ')}
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
        data-end-slot-button={
          React.Children.toArray(children).some(isButtonElement)
            ? 'true'
            : undefined
        }
      >
        {processedChildren}
      </div>
    );
  },
);
EndSlot.displayName = 'Input.EndSlot';

// StartIcon and EndIcon with shared implementation
const StartIcon = forwardRef<HTMLDivElement, SlotProps>(
  ({ children, className }, ref) => {
    const state = useContext(InputContext);

    const processedChildren = passthroughChildrenWithDisabledState(
      children,
      state.isDisabled,
    );

    return (
      <div
        ref={ref}
        className={`
          ${styles['input-field__startIcon']} 
          ${className || ''}
        `}
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
      >
        {processedChildren}
      </div>
    );
  },
);
StartIcon.displayName = 'Input.StartIcon';

const EndIcon = forwardRef<HTMLDivElement, SlotProps>(
  ({ children, className }, ref) => {
    const state = useContext(InputContext);

    const processedChildren = passthroughChildrenWithDisabledState(
      children,
      state.isDisabled,
    );

    return (
      <div
        ref={ref}
        className={`
          ${styles['input-field__endIcon']} 
          ${className || ''}
        `}
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
      >
        {processedChildren}
      </div>
    );
  },
);
EndIcon.displayName = 'Input.EndIcon';

// StartSelect and EndSelect Subcomponents
interface SelectProps {
  children: React.ReactElement;
  className?: string;
}

const StartSelect = forwardRef<HTMLDivElement, SelectProps>(
  ({ children, className }, ref) => {
    const state = useContext(InputContext);

    // Validate that only select element is passed
    const isSelectElement =
      isValidElement(children) &&
      (children.type === 'select' ||
        (typeof children.type === 'string' &&
          children.type.toLowerCase() === 'select'));

    if (!isSelectElement) {
      altoConsole.error('Input.StartSelect only accepts a select element', {
        scope: 'Input',
      });
    }

    return (
      <div
        ref={ref}
        className={`
          ${styles['input-field__startSelect']} 
          ${className || ''}
        `}
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
      >
        {React.cloneElement(
          children as React.ReactElement<
            React.SelectHTMLAttributes<HTMLSelectElement>
          >,
          {
            disabled: state.isDisabled,
            onFocusCapture: () => {
              if (ref && 'current' in ref && ref.current) {
                ref.current.dataset.focused = 'true';
                ref.current.dataset.focusVisible = 'true';
              }
            },
            onBlurCapture: () => {
              if (ref && 'current' in ref && ref.current) {
                delete ref.current.dataset.focused;
                delete ref.current.dataset.focusVisible;
              }
            },
          },
        )}
      </div>
    );
  },
);
StartSelect.displayName = 'Input.StartSelect';

const EndSelect = forwardRef<HTMLDivElement, SelectProps>(
  ({ children, className }, ref) => {
    const state = useContext(InputContext);

    // Validate that only select element is passed
    const isSelectElement =
      isValidElement(children) &&
      (children.type === 'select' ||
        (typeof children.type === 'string' &&
          children.type.toLowerCase() === 'select'));

    if (!isSelectElement) {
      altoConsole.error('Input.EndSelect only accepts a select element', {
        scope: 'Input',
      });
    }

    return (
      <div
        ref={ref}
        className={`
          ${styles['input-field__endSelect']} 
          ${className || ''}
        `}
        {...(state.isDisabled ? { 'data-disabled': true } : {})}
        {...(state.isInvalid ? { 'data-invalid': true } : {})}
      >
        {React.cloneElement(
          children as React.ReactElement<
            React.SelectHTMLAttributes<HTMLSelectElement>
          >,
          {
            disabled: state.isDisabled,
            onFocusCapture: () => {
              if (ref && 'current' in ref && ref.current) {
                ref.current.dataset.focused = 'true';
                ref.current.dataset.focusVisible = 'true';
              }
            },
            onBlurCapture: () => {
              if (ref && 'current' in ref && ref.current) {
                delete ref.current.dataset.focused;
                delete ref.current.dataset.focusVisible;
              }
            },
          },
        )}
      </div>
    );
  },
);
EndSelect.displayName = 'Input.EndSelect';

type ResponsivePropKeys = 'fieldColumns' | 'fieldMaxWidthByChars';
interface BaseInputProps extends Omit<TextFieldProps, 'children'> {
  /**
   * Custom field component to render (e.g., AriaNumberField)
   */
  as?: React.ComponentType<object>;
  /**
   * Label for the input field
   */
  label?: string;

  /**
   * Description text for the input
   */
  description?: string;

  /**
   * Placeholder text for the input
   */
  placeholder?: string;

  /**
   * Error message for the input
   */
  errorMessage?: string | ((validation: ValidationResult) => string);

  /**
   * Additional props to pass to the underlying input element
   */
  inputProps?: Omit<AriaInputProps, 'className'>;

  /**
   * Additional class name for the input wrapper
   */
  className?: string;

  /**
   * Children for Input subcomponents
   */
  children?: React.ReactNode;

  /**
   * Whether the input can be cleared
   * @default false
   */
  clearable?: boolean;

  /**
   * Tooltip text for the clear button
   * @default 'Clear input'
   */
  clearButtonTooltip?: string;

  /**
   * Accessible label for the clear button
   * @default 'Clear input'
   */
  clearButtonAriaLabel?: string;

  /**
   * The number of columns the Field should have
   */
  fieldColumns?: InputFieldVariants['fieldColumns'];
  /**
   * Field width based on a rough estimate of the number of characters
   */
  fieldMaxWidthByChars?: InputFieldVariants['fieldMaxWidthByChars'];
  /**
   * The placement of the error message
   * @default 'top'
   */
  errorPlacement?: 'top' | 'bottom' /**
   * The translation for the optional text, used for localization
   * @default 'Optional'
   */;
  optionalTranslation?: string;
  /**
   * The id of the error message element
   */
  errorId?: string;
  /**
   * Prefix text for screen readers only in error messages
   * @default 'Error:'
   */
  errorPrefix?: string;
  /**
   * Additional context for screen readers, rendered visually hidden next to the label.
   */
  visuallyHiddenLabel?: string;
}

export type InputProps = WithResponsiveProps<
  BaseInputProps,
  ResponsivePropKeys
>;

// Define the Input component type
type InputComponent = React.ForwardRefExoticComponent<
  InputProps & React.RefAttributes<HTMLInputElement>
> & {
  StartSlot: typeof StartSlot;
  EndSlot: typeof EndSlot;
  Prefix: typeof Prefix;
  Suffix: typeof Suffix;
  StartSelect: typeof StartSelect;
  EndSelect: typeof EndSelect;
  StartIcon: typeof StartIcon;
  EndIcon: typeof EndIcon;
};

const measureSlotWidths = (
  wrapperRef: React.RefObject<HTMLDivElement>,
  refs: {
    startSlotRef: React.RefObject<HTMLDivElement>;
    endSlotRef: React.RefObject<HTMLDivElement>;
    prefixRef: React.RefObject<HTMLDivElement>;
    suffixRef: React.RefObject<HTMLDivElement>;
    startSelectRef: React.RefObject<HTMLDivElement>;
    endSelectRef: React.RefObject<HTMLDivElement>;
    startIconRef: React.RefObject<HTMLDivElement>;
    endIconRef: React.RefObject<HTMLDivElement>;
  },
) => {
  const wrapper = wrapperRef.current;
  if (!wrapper) return;

  startMeasureWidthBatch();

  const startSlotWidth = measureWidth(refs.startSlotRef);
  const endSlotWidth = measureWidth(refs.endSlotRef);
  const prefixWidth = measureWidth(refs.prefixRef);
  const suffixWidth = measureWidth(refs.suffixRef);
  const startSelectWidth = measureWidth(refs.startSelectRef);
  const endSelectWidth = measureWidth(refs.endSelectRef);
  const startIconWidth = measureWidth(refs.startIconRef);
  const endIconWidth = measureWidth(refs.endIconRef);

  endMeasureWidthBatch();

  const startWidth =
    startSlotWidth || prefixWidth || startSelectWidth || startIconWidth;
  if (startWidth > 0) {
    wrapper.style.setProperty('--input-start-slot-width', `${startWidth}px`);
    wrapper.dataset.startSlot = 'true';
  } else {
    wrapper.style.removeProperty('--input-start-slot-width');
    delete wrapper.dataset.startSlot;
  }

  const endWidth =
    endSlotWidth || suffixWidth || endSelectWidth || endIconWidth;
  if (endWidth > 0) {
    wrapper.style.setProperty('--input-end-slot-width', `${endWidth}px`);
    wrapper.dataset.endSlot = 'true';
  } else {
    wrapper.style.removeProperty('--input-end-slot-width');
    delete wrapper.dataset.endSlot;
  }
};

const hasButtonOrIconButton = (
  slot: React.ReactElement | undefined,
): boolean => {
  if (!slot) return false;
  const slotChildren = React.Children.toArray(slot.props.children);
  return slotChildren.some(
    (child) => isButtonElement(child) || isIconButtonElement(child),
  );
};

const resolveSearchInputConfig = (
  clearable: boolean,
  inputType: string | undefined,
) => {
  const baseStyle = styles['input-field__input'];
  if (inputType !== 'search') {
    return {
      effectiveClearable: clearable,
      inputFieldType: inputType,
      inputFieldStyle: baseStyle,
    };
  }
  return {
    effectiveClearable: true,
    inputFieldType: 'search' as typeof inputType,
    inputFieldStyle: `${baseStyle} ${styles['input-field__input--hideSearchClear']}`,
  };
};

const resolveInputFieldClasses = (
  fieldColumns: unknown,
  fieldMaxWidthByChars: unknown,
  resolvedColumns: InputFieldVariants['fieldColumns'],
  resolvedMaxWidthByChars: InputFieldVariants['fieldMaxWidthByChars'],
  columnsResp: string[],
  maxWidthByCharsResp: string[],
): { baseClass: string; responsiveClasses: string[] } => {
  if (fieldColumns && fieldMaxWidthByChars) {
    return {
      baseClass: inputFieldVariants({
        fieldColumns: resolvedColumns,
        fieldMaxWidthByChars: resolvedMaxWidthByChars,
      }),
      responsiveClasses: [...columnsResp, ...maxWidthByCharsResp],
    };
  }
  if (fieldColumns) {
    return {
      baseClass: inputFieldVariants({ fieldColumns: resolvedColumns }),
      responsiveClasses: columnsResp,
    };
  }
  if (fieldMaxWidthByChars) {
    return {
      baseClass: inputFieldVariants({
        fieldMaxWidthByChars: resolvedMaxWidthByChars,
      }),
      responsiveClasses: maxWidthByCharsResp,
    };
  }
  return { baseClass: inputFieldVariants({}), responsiveClasses: [] };
};

const handleClearClick = (
  e: React.MouseEvent,
  inputElementRef: React.MutableRefObject<HTMLInputElement | null>,
  inputProps: Omit<AriaInputProps, 'className'> | undefined,
) => {
  e.preventDefault();
  e.stopPropagation();
  if (!inputElementRef.current) return;
  inputElementRef.current.focus();
  if (inputProps && 'value' in inputProps) {
    inputProps.onChange?.({
      target: { value: '' },
    } as React.ChangeEvent<HTMLInputElement>);
  }
};

const assignInputRefs = (
  el: HTMLInputElement | null,
  forwardedRef: React.ForwardedRef<HTMLInputElement>,
  inputElementRef: React.MutableRefObject<HTMLInputElement | null>,
) => {
  if (typeof forwardedRef === 'function') {
    forwardedRef(el);
  } else if (forwardedRef && 'current' in forwardedRef) {
    (forwardedRef as React.MutableRefObject<HTMLInputElement | null>).current =
      el;
  }
  inputElementRef.current = el;
};

const resolveStartContent = (
  startIconChildren: React.ReactElement | undefined,
  startSelectChildren: React.ReactElement | undefined,
  startSlotChildren: React.ReactElement | undefined,
  prefixChildren: React.ReactElement | undefined,
  refs: {
    startIconRef: React.RefObject<HTMLDivElement>;
    startSelectRef: React.RefObject<HTMLDivElement>;
    startSlotRef: React.RefObject<HTMLDivElement>;
    prefixRef: React.RefObject<HTMLDivElement>;
  },
  visuallyHiddenLabel: string | undefined,
): React.ReactElement | null => {
  if (startIconChildren)
    return React.cloneElement(startIconChildren, { ref: refs.startIconRef });
  if (startSelectChildren)
    return React.cloneElement(startSelectChildren, {
      ref: refs.startSelectRef,
    });
  if (startSlotChildren)
    return React.cloneElement(startSlotChildren, { ref: refs.startSlotRef });
  if (prefixChildren)
    return React.cloneElement(prefixChildren, {
      ref: refs.prefixRef,
      visuallyHiddenLabel,
    });
  return null;
};

const resolveEndContent = (
  endIconChildren: React.ReactElement | undefined,
  endSelectChildren: React.ReactElement | undefined,
  endSlotChildren: React.ReactElement | undefined,
  suffixChildren: React.ReactElement | undefined,
  refs: {
    endIconRef: React.RefObject<HTMLDivElement>;
    endSelectRef: React.RefObject<HTMLDivElement>;
    endSlotRef: React.RefObject<HTMLDivElement>;
    suffixRef: React.RefObject<HTMLDivElement>;
  },
  visuallyHiddenLabel: string | undefined,
): React.ReactElement | null => {
  if (endIconChildren)
    return React.cloneElement(endIconChildren, { ref: refs.endIconRef });
  if (endSelectChildren)
    return React.cloneElement(endSelectChildren, { ref: refs.endSelectRef });
  if (endSlotChildren)
    return React.cloneElement(endSlotChildren, { ref: refs.endSlotRef });
  if (suffixChildren)
    return React.cloneElement(suffixChildren, {
      ref: refs.suffixRef,
      visuallyHiddenLabel,
    });
  return null;
};

const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      description,
      placeholder,
      fieldColumns,
      fieldMaxWidthByChars,
      isInvalid,
      isDisabled = false,
      isRequired = false,
      inputProps,
      children,
      clearable = false,
      clearButtonAriaLabel = 'Clear input',
      clearButtonTooltip = clearButtonAriaLabel,
      optionalTranslation,
      errorMessage,
      errorId,
      errorPrefix,
      errorPlacement = 'top',
      as: FieldComponent = TextField,
      visuallyHiddenLabel,
      ...props
    },
    ref,
  ) => {
    const formValidationErrors = React.useContext(FormValidationContext);
    const fieldName = typeof props.name === 'string' ? props.name : undefined;
    const { effectiveErrorMessage, effectiveIsInvalid } =
      resolveValidationState({
        isInvalid,
        errorMessage,
        fieldName,
        validationErrors: formValidationErrors,
      });

    // Responsive helpers
    const getResponsiveClasses = generateResponsiveClasses(inputStyles);
    const breakpoint = useBreakpoint();
    // SSR fallback base values
    const baseColumns = getBaseValue(fieldColumns, 'twelve');
    const baseMaxWidthByChars = getBaseValue(fieldMaxWidthByChars, 'ten');
    // Runtime-resolved values for dynamic class
    const resolvedColumns =
      resolveResponsiveProp(fieldColumns, breakpoint) ?? baseColumns;
    const resolvedMaxWidthByChars =
      resolveResponsiveProp(fieldMaxWidthByChars, breakpoint) ??
      baseMaxWidthByChars;
    // Responsive classes for SSR (all breakpoints)
    const columnsResp = getResponsiveClasses(
      fieldColumns,
      'input-field-variant--columns',
    );
    const maxWidthByCharsResp = getResponsiveClasses(
      fieldMaxWidthByChars,
      'input-field-variant--max-width-by-chars',
    );

    const inputType = inputProps?.type || props.type;
    const { effectiveClearable, inputFieldType, inputFieldStyle } =
      resolveSearchInputConfig(clearable, inputType);
    const inputId = React.useId();
    const wrapperRef = useRef<HTMLDivElement>(null);
    const startSlotRef = useRef<HTMLDivElement>(null);
    const endSlotRef = useRef<HTMLDivElement>(null);
    const prefixRef = useRef<HTMLDivElement>(null);
    const suffixRef = useRef<HTMLDivElement>(null);
    const startSelectRef = useRef<HTMLDivElement>(null);
    const endSelectRef = useRef<HTMLDivElement>(null);
    const inputElementRef = useRef<HTMLInputElement>(null);
    const clearButtonRef = useRef<HTMLDivElement>(null);
    const startIconRef = useRef<HTMLDivElement>(null);
    const endIconRef = useRef<HTMLDivElement>(null);

    // In the component
    useLayoutEffect(() => {
      measureSlotWidths(wrapperRef, {
        startSlotRef,
        endSlotRef,
        prefixRef,
        suffixRef,
        startSelectRef,
        endSelectRef,
        startIconRef,
        endIconRef,
      });
    }, [children]);

    useEffect(() => {
      const timer = setTimeout(() => {
        measureSlotWidths(wrapperRef, {
          startSlotRef,
          endSlotRef,
          prefixRef,
          suffixRef,
          startSelectRef,
          endSelectRef,
          startIconRef,
          endIconRef,
        });
      }, 100);

      // Cleanup to prevent memory leaks
      return () => clearTimeout(timer);
    }, [children]);

    // Extract all possible children components
    const childrenArray = React.Children.toArray(children);

    const startSlotChildren = childrenArray.find(
      (child) => React.isValidElement(child) && child.type === StartSlot,
    ) as React.ReactElement | undefined;

    const endSlotChildren = childrenArray.find(
      (child) => React.isValidElement(child) && child.type === EndSlot,
    ) as React.ReactElement | undefined;

    const hasStartSlotButton = hasButtonOrIconButton(startSlotChildren);
    const hasEndSlotButton = hasButtonOrIconButton(endSlotChildren);

    const prefixChildren = childrenArray.find(
      (child) => React.isValidElement(child) && child.type === Prefix,
    ) as React.ReactElement | undefined;

    const suffixChildren = childrenArray.find(
      (child) => React.isValidElement(child) && child.type === Suffix,
    ) as React.ReactElement | undefined;

    const startSelectChildren = childrenArray.find(
      (child) => React.isValidElement(child) && child.type === StartSelect,
    ) as React.ReactElement | undefined;

    const endSelectChildren = childrenArray.find(
      (child) => React.isValidElement(child) && child.type === EndSelect,
    ) as React.ReactElement | undefined;

    const startIconChildren = childrenArray.find(
      (child) => React.isValidElement(child) && child.type === StartIcon,
    ) as React.ReactElement | undefined;

    const endIconChildren = childrenArray.find(
      (child) => React.isValidElement(child) && child.type === EndIcon,
    ) as React.ReactElement | undefined;

    // Determine if clear button should be shown
    const shouldShowClearButton =
      effectiveClearable &&
      inputProps?.value != null &&
      String(inputProps.value).length > 0 &&
      !isDisabled;

    const { baseClass, responsiveClasses } = resolveInputFieldClasses(
      fieldColumns,
      fieldMaxWidthByChars,
      resolvedColumns,
      resolvedMaxWidthByChars,
      columnsResp,
      maxWidthByCharsResp,
    );

    const clearButtonElement = shouldShowClearButton ? (
      <div ref={clearButtonRef}>
        <IconButton
          iconProps={{
            iconName: 'circle-xmark',
            iconPrefix: 'fas',
          }}
          emphasis="quaternary"
          size="sm"
          styleVariant="neutral"
          onClick={(e) =>
            handleClearClick(
              e,
              inputElementRef as React.MutableRefObject<HTMLInputElement | null>,
              inputProps,
            )
          }
          aria-label={clearButtonAriaLabel}
          tooltipLabel={clearButtonTooltip}
        />
      </div>
    ) : null;

    return (
      <FieldComponent
        {...props}
        isDisabled={isDisabled}
        {...(effectiveErrorMessage || effectiveIsInvalid
          ? { isInvalid: effectiveIsInvalid }
          : {})}
        className={`
          ${styles['input-field']} 
        `}
      >
        {/* Always render FieldHeader for desc/error display, even if label is missing */}
        {(label || description || errorPlacement === 'top') && (
          <FieldHeader
            label={label}
            labelId={inputProps?.['aria-labelledby']}
            labelFor={inputProps?.id}
            description={description}
            descriptionId={inputProps?.['aria-describedby']}
            optionalTranslation={optionalTranslation}
            isInvalid={effectiveIsInvalid}
            isRequired={isRequired}
            errorMessage={effectiveErrorMessage}
            showError={errorPlacement === 'top'}
            errorId={errorId}
            errorPrefix={errorPrefix}
            visuallyHiddenLabel={visuallyHiddenLabel}
          />
        )}

        <InputContext.Provider
          value={{
            isDisabled,
            isInvalid: effectiveIsInvalid,
            inputId,
          }}
        >
          <div className={styles['input-field__grid']}>
            <div
              ref={wrapperRef}
              className={[
                styles['input-field__wrapper'],
                inputStyles['input-field-variant'],
                baseClass,
                ...responsiveClasses,
              ]
                .filter(Boolean)
                .join(' ')}
              {...(hasStartSlotButton
                ? { 'data-has-start-button': 'true' }
                : {})}
              {...(hasEndSlotButton ? { 'data-has-end-button': 'true' } : {})}
            >
              {/* Render StartIcon, StartSelect, StartSlot, or Prefix */}
              {resolveStartContent(
                startIconChildren,
                startSelectChildren,
                startSlotChildren,
                prefixChildren,
                { startIconRef, startSelectRef, startSlotRef, prefixRef },
                visuallyHiddenLabel,
              )}

              <AriaInput
                ref={(el) =>
                  assignInputRefs(
                    el,
                    ref,
                    inputElementRef as React.MutableRefObject<HTMLInputElement | null>,
                  )
                }
                {...inputProps}
                type={inputFieldType}
                placeholder={placeholder}
                className={inputFieldStyle}
                data-disabled={isDisabled ? true : undefined}
                data-invalid={effectiveIsInvalid ? true : undefined}
                data-required={isRequired ? true : undefined}
                disabled={isDisabled ? true : undefined}
              />

              {effectiveClearable && (
                <div className={styles['input-field__clearButton']}>
                  {clearButtonElement}
                </div>
              )}

              {/* Render EndIcon, EndSelect, EndSlot, or Suffix */}
              {resolveEndContent(
                endIconChildren,
                endSelectChildren,
                endSlotChildren,
                suffixChildren,
                { endIconRef, endSelectRef, endSlotRef, suffixRef },
                visuallyHiddenLabel,
              )}
            </div>
          </div>
        </InputContext.Provider>
        {errorPlacement === 'bottom' ? (
          <div className={styles['input-field__errorMessage']}>
            <FieldError
              id={errorId}
              fieldLabel={label}
              errorPrefix={errorPrefix}
            >
              {effectiveErrorMessage}
            </FieldError>
          </div>
        ) : null}
      </FieldComponent>
    );
  },
) as InputComponent;

Input.displayName = 'Input';

// Attach subcomponents
Input.StartSlot = StartSlot;
Input.EndSlot = EndSlot;
Input.Prefix = Prefix;
Input.Suffix = Suffix;
Input.StartSelect = StartSelect;
Input.EndSelect = EndSelect;
Input.StartIcon = StartIcon;
Input.EndIcon = EndIcon;

export default Input;
